import { useEffect, useState } from 'react';

/**
 * Parte de la pantalla que se ve de verdad cuando el teclado del móvil está
 * abierto. En el iPhone (y en Chrome desde la v108) el teclado no encoge la
 * página: tapa lo de abajo, y una hoja pegada abajo con `position: fixed`
 * queda escondida debajo del teclado. Además Safari desplaza la vista para
 * enseñar el campo, a veces a medias (Sergi, 27-oct-2026: «Editar categoría»
 * se quedaba tapada casi siempre y había que salir y volver a entrar).
 */
export type CajaVisible = { arriba: number; alto: number };

/** Menos de esto de diferencia no es un teclado (barras del navegador). */
const MINIMO_TECLADO = 120;

type Ventana = {
  /** Alto de la página (`window.innerHeight`). */
  altoPagina: number;
  /** `visualViewport.height`. */
  altoVisible: number;
  /** `visualViewport.offsetTop`. */
  desplazamiento: number;
  /** Zoom de la raíz que pone `escala.js` en algunos navegadores (1 = nada). */
  zoom: number;
};

/**
 * Si el teclado tapa parte de la página, la caja (en px de la página) donde
 * cabe una hoja; `null` si se ve todo y basta con `inset: 0`.
 */
export function cajaVisible({
  altoPagina,
  altoVisible,
  desplazamiento,
  zoom,
}: Ventana): CajaVisible | null {
  if (!(altoPagina > 0) || !(altoVisible > 0)) {
    return null;
  }
  if (altoPagina - altoVisible < MINIMO_TECLADO) {
    return null;
  }
  const z = zoom > 0 ? zoom : 1;
  return {
    arriba: Math.max(0, desplazamiento) / z,
    alto: altoVisible / z,
  };
}

function medir(): CajaVisible | null {
  const vv = window.visualViewport;
  if (!vv) {
    return null;
  }
  return cajaVisible({
    altoPagina: window.innerHeight,
    altoVisible: vv.height,
    desplazamiento: vv.offsetTop,
    zoom:
      (window as Window & { __presupuestoZoom?: number }).__presupuestoZoom ??
      1,
  });
}

/**
 * La caja visible, al día mientras el teclado sube, baja o Safari desplaza
 * la vista. Al cerrarse el teclado devuelve el documento arriba: la página no
 * tiene scroll propio y, si Safari la dejó desplazada, se veía cortada.
 */
export function useCajaVisible(): CajaVisible | null {
  const [caja, setCaja] = useState<CajaVisible | null>(medir);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }
    let teclado = false;
    const actualizar = () => {
      const nueva = medir();
      setCaja(previa =>
        previa?.arriba === nueva?.arriba && previa?.alto === nueva?.alto
          ? previa
          : nueva,
      );
      if (teclado && !nueva) {
        devolverArriba();
      }
      teclado = nueva != null;
    };
    actualizar();
    vv.addEventListener('resize', actualizar);
    vv.addEventListener('scroll', actualizar);
    return () => {
      vv.removeEventListener('resize', actualizar);
      vv.removeEventListener('scroll', actualizar);
      if (teclado) {
        devolverArriba();
      }
    };
  }, []);

  return caja;
}

function devolverArriba() {
  const doc = document.scrollingElement;
  if (doc && doc.scrollTop !== 0) {
    doc.scrollTop = 0;
  }
}
